import {
  AdditionalFrequency,
  IProduct,
  IRateReqSchema,
  IRateReqSchemaUI,
  OfferingTypesEnum,
  RateReqSchema,
} from '@monetize/types/app';
import isNil from 'lodash/isNil';
import { UseFormSetError } from 'react-hook-form';
import { validateBlockPricing } from '../../../../../../components/Quotes/PricesDrawer/utils';
import { logger } from '../../../../../../services/logger';
import Sentry from '../../../../../../services/sentry';
import { transformFormDataToRateRequest } from '../../../../../../utils/rateFormUtils';
import { getAdditionalFrequencyData } from '../RatePriceUtils';

interface UseRateFormSubmitParams {
  offeringType: OfferingTypesEnum;
  isCreditAllocationFrequencyRequired: boolean;
  setError: UseFormSetError<IRateReqSchemaUI>;
}

/**
 * Custom hook to handle rate form validation logic
 */
export const useRateFormValidation = ({
  offeringType,
  isCreditAllocationFrequencyRequired,
  setError,
}: UseRateFormSubmitParams) => {
  const validateCreditAllocation = (formData: IRateReqSchemaUI): boolean => {
    if (
      isCreditAllocationFrequencyRequired &&
      (isNil(formData.options?.tokenAllocationFrequencyInMonth) ||
        formData.options?.tokenAllocationFrequencyInMonth < 1)
    ) {
      setError('options.tokenAllocationFrequencyInMonth', {
        type: 'required',
        message: 'Credit allocation frequency is required',
      });
      logger.error('Credit allocation frequency is required');
      return false;
    }
    return true;
  };

  const validatePercentOfTotal = (formData: IRateReqSchemaUI): boolean => {
    if (offeringType === OfferingTypesEnum.CUSTOM_PERCENT_OF_TOTAL) {
      const minTotal = formData.percentOfTotalConfig?.minimumTotal ?? null;
      const maxTotal = formData.percentOfTotalConfig?.maximumTotal ?? null;

      if (minTotal !== null && maxTotal !== null && minTotal > maxTotal) {
        setError('percentOfTotalConfig.maximumTotal', {
          type: 'custom',
          message:
            'Maximum Total must be greater than or equal to Minimum Total',
        });
        logger.error(
          'Maximum Total must be greater than or equal to Minimum Total',
        );
        return false;
      }
    }
    return true;
  };

  const validateForm = (formData: IRateReqSchemaUI): boolean => {
    if (!validateCreditAllocation(formData)) {
      return false;
    }

    if (!validatePercentOfTotal(formData)) {
      return false;
    }

    if (validateBlockPricing(formData, setError)) {
      return false;
    }

    return true;
  };

  return { validateForm };
};

/**
 * Helper to parse and prepare rate data for submission
 */
export const prepareRatePayload = (
  finalData: IRateReqSchema,
  errorContext: string,
): IRateReqSchema => {
  const parsed = RateReqSchema.safeParse(finalData);

  if (!parsed.success) {
    Sentry.captureException(parsed.error.issues, {
      tags: {
        type: errorContext,
      },
    });
  }

  return parsed.success ? parsed.data : finalData;
};

/**
 * Helper to create additional frequency rate data
 */
export const createAdditionalFrequencyData = (
  products: IProduct[],
  frequency: AdditionalFrequency,
  finalData: IRateReqSchema,
): IRateReqSchema => {
  const additionalFrequencyData = getAdditionalFrequencyData(
    products,
    frequency,
    finalData,
  );

  const parsedAdditionalRate = RateReqSchema.safeParse(additionalFrequencyData);

  return parsedAdditionalRate.success
    ? parsedAdditionalRate.data
    : additionalFrequencyData;
};

/**
 * Transforms form data to final request payload with validation
 */
export const prepareFormSubmission = (
  formData: IRateReqSchemaUI,
  products: IProduct[],
  offeringType: OfferingTypesEnum,
): IRateReqSchema => {
  return transformFormDataToRateRequest(formData, products, offeringType);
};
